<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import {
  apiErrorCode,
  apiErrorDetail,
  apiErrorMessage,
} from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import {
  PURPOSES,
  MAX_OFFER_SLOTS,
  toggleSlot,
  visitBody,
} from '../ticketHelpers';
import FormField from './FormField.vue';

const MIN_MINUTES = 15;
const MAX_MINUTES = 480;
const REASON_MAX = 1000;

// Arrange a repair visit for a ticket: send the tenant 1-3 options ("offer"),
// book one slot now ("book"), or move a booked visit ("reschedule"). The free
// slots are the backend's: they already respect the person in charge's
// coverage, leave, the working hours and holidays.
export default {
  name: 'BookingModal',
  components: { BaseModal, FormField },
  props: {
    ticket: { type: Object, required: true },
    // A booked visit to move; without it the modal arranges a new one.
    visit: { type: Object, default: null },
  },
  emits: ['close', 'done'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      purposes: PURPOSES,
      minMinutes: MIN_MINUTES,
      maxMinutes: MAX_MINUTES,
      mode: 'offer',
      purpose: PURPOSES[0],
      minutes: '',
      reason: '',
      slots: null,
      selected: [],
      isLoading: false,
      isSaving: false,
      error: '',
    };
  },
  computed: {
    isReschedule() {
      return !!this.visit;
    },
    maxSlots() {
      return this.isReschedule || this.mode === 'book' ? 1 : MAX_OFFER_SLOTS;
    },
    days() {
      return this.slots ? this.slots.days : [];
    },
    canSubmit() {
      return this.selected.length > 0 && !this.isSaving;
    },
  },
  watch: {
    mode() {
      // Book takes exactly one slot: keep the first pick.
      if (this.selected.length > this.maxSlots) {
        this.selected = this.selected.slice(0, this.maxSlots);
      }
    },
  },
  mounted() {
    if (this.visit) this.minutes = this.visit.minutes;
    this.loadSlots();
  },
  methods: {
    ...mapActions('coLivingTickets', [
      'fetchSlots',
      'sendVisit',
      'visitAction',
    ]),
    async loadSlots() {
      this.isLoading = true;
      this.error = '';
      const params = {};
      const minutes = Number(this.minutes);
      if (
        Number.isInteger(minutes) &&
        minutes >= MIN_MINUTES &&
        minutes <= MAX_MINUTES
      ) {
        params.minutes = minutes;
      }
      try {
        this.slots = await this.fetchSlots({ id: this.ticket.id, params });
        // The server's length is the one used for the slots shown.
        this.minutes = this.slots.minutes;
      } catch (err) {
        this.error = apiErrorMessage(
          this,
          err,
          'CO_LIVING.TICKETS.BOOKING.LOAD_FAILED'
        );
      } finally {
        this.isLoading = false;
      }
    },
    isSelected(date, slot) {
      return this.selected.some(s => s.date === date && s.start === slot.start);
    },
    pick(day, slot) {
      this.selected = toggleSlot(
        this.selected,
        { date: day.date, start: slot.start, label: slot.label },
        this.maxSlots
      );
    },
    reasonText(day) {
      const key = `CO_LIVING.TICKETS.BOOKING.REASONS.${String(
        day.reason || ''
      ).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : day.reason_text || day.reason;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.error = '';
      this.isSaving = true;
      try {
        if (this.isReschedule) {
          const [slot] = this.selected;
          await this.visitAction({
            ticketId: this.ticket.id,
            visitId: this.visit.id,
            action: 'reschedule',
            payload: {
              date: slot.date,
              start: slot.start,
              reason: this.reason.trim().slice(0, REASON_MAX),
            },
          });
        } else {
          await this.sendVisit({
            id: this.ticket.id,
            body: visitBody({
              mode: this.mode,
              purpose: this.purpose,
              minutes: this.minutes,
              slots: this.selected,
            }),
          });
        }
        useAlert(this.$t('CO_LIVING.TICKETS.BOOKING.DONE'));
        this.$emit('done');
        this.$emit('close');
      } catch (err) {
        await this.showError(err);
      } finally {
        this.isSaving = false;
      }
    },
    // A slot that was just taken is the usual refusal: say so, drop the
    // selection and show the fresh slots.
    async showError(err) {
      const code = apiErrorCode(err);
      if (code === 'slot_unavailable') {
        const { reason } = apiErrorDetail(err);
        this.selected = [];
        await this.loadSlots();
        // Set after the reload, which clears the error.
        this.error = this.$t('CO_LIVING.TICKETS.BOOKING.SLOT_UNAVAILABLE', {
          reason: reason || '',
        });
        return;
      }
      this.error = apiErrorMessage(
        this,
        err,
        'CO_LIVING.TICKETS.BOOKING.FAILED'
      );
      // The ticket moved on (not waiting for a slot any more): refresh it.
      if (code === 'visit_not_needed' || code === 'visit_not_booked') {
        this.$emit('done');
      }
    },
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      {{
        isReschedule
          ? $t('CO_LIVING.TICKETS.BOOKING.RESCHEDULE_TITLE')
          : $t('CO_LIVING.TICKETS.BOOKING.TITLE')
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-booking-form"
      @submit.prevent="submit"
    >
      <p class="text-sm text-slate-600 dark:text-slate-300">
        <template v-if="slots && slots.member">
          {{
            $t('CO_LIVING.TICKETS.BOOKING.WITH', { name: slots.member.name })
          }}
        </template>
        <template v-else-if="slots">
          {{ $t('CO_LIVING.TICKETS.BOOKING.NO_PIC') }}
        </template>
      </p>

      <fieldset v-if="!isReschedule" class="flex flex-wrap gap-4">
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="mode"
            type="radio"
            value="offer"
            class="mb-0"
            data-testid="co-living-booking-mode-offer"
          />
          {{ $t('CO_LIVING.TICKETS.BOOKING.MODE_OFFER') }}
        </label>
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="mode"
            type="radio"
            value="book"
            class="mb-0"
            data-testid="co-living-booking-mode-book"
          />
          {{ $t('CO_LIVING.TICKETS.BOOKING.MODE_BOOK') }}
        </label>
      </fieldset>

      <div v-if="!isReschedule" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          :label="$t('CO_LIVING.TICKETS.BOOKING.PURPOSE')"
          for-id="co-living-booking-purpose"
        >
          <select
            id="co-living-booking-purpose"
            v-model="purpose"
            :class="selectClass"
            data-testid="co-living-booking-purpose"
          >
            <option v-for="p in purposes" :key="p" :value="p">{{ p }}</option>
          </select>
        </FormField>
        <FormField
          :label="$t('CO_LIVING.TICKETS.BOOKING.MINUTES')"
          for-id="co-living-booking-minutes"
          :hint="$t('CO_LIVING.TICKETS.BOOKING.MINUTES_HINT')"
        >
          <input
            id="co-living-booking-minutes"
            v-model="minutes"
            type="number"
            :min="minMinutes"
            :max="maxMinutes"
            :class="fieldClass"
            data-testid="co-living-booking-minutes"
            @change="loadSlots"
          />
        </FormField>
      </div>

      <FormField
        v-else
        :label="$t('CO_LIVING.TICKETS.BOOKING.REASON')"
        for-id="co-living-booking-reason"
      >
        <input
          id="co-living-booking-reason"
          v-model="reason"
          type="text"
          maxlength="1000"
          :class="fieldClass"
          data-testid="co-living-booking-reason"
        />
      </FormField>

      <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
        {{
          $t(
            maxSlots === 1
              ? 'CO_LIVING.TICKETS.BOOKING.PICK_ONE'
              : 'CO_LIVING.TICKETS.BOOKING.PICK_UP_TO',
            { max: maxSlots }
          )
        }}
        <span
          class="font-normal text-slate-500"
          data-testid="co-living-booking-count"
        >
          ({{ selected.length }}/{{ maxSlots }})
        </span>
      </p>

      <p
        v-if="isLoading"
        class="text-sm text-slate-500"
        data-testid="co-living-booking-loading"
      >
        {{ $t('CO_LIVING.COMMON.LOADING') }}
      </p>
      <div
        v-else
        class="max-h-72 overflow-y-auto flex flex-col gap-2"
        data-testid="co-living-booking-days"
      >
        <div
          v-for="day in days"
          :key="day.date"
          class="p-2 border rounded border-slate-200 dark:border-slate-700"
          :data-testid="`co-living-booking-day-${day.date}`"
        >
          <p class="text-sm text-slate-900 dark:text-slate-100">
            {{ day.weekday }} {{ day.date }}
            <span
              v-if="!day.is_available"
              class="ml-2 text-xs text-slate-500"
              :data-testid="`co-living-booking-reason-${day.date}`"
            >
              {{ reasonText(day) }}
            </span>
          </p>
          <div v-if="day.is_available" class="flex flex-wrap gap-2 mt-1">
            <button
              v-for="slot in day.slots"
              :key="slot.start"
              type="button"
              class="px-2 py-1 text-xs border rounded"
              :class="
                isSelected(day.date, slot)
                  ? 'bg-woot-500 text-white border-woot-500'
                  : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-woot-500 hover:bg-woot-50 dark:hover:bg-slate-700 transition-colors'
              "
              :data-testid="`co-living-slot-${day.date}-${slot.start}`"
              @click="pick(day, slot)"
            >
              {{ slot.start }}-{{ slot.end }}
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="error"
        class="text-sm text-red-500"
        data-testid="co-living-booking-error"
      >
        {{ error }}
      </p>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :disabled="!canSubmit"
          :is-loading="isSaving"
          data-testid="co-living-booking-submit"
        >
          {{
            $t(
              isReschedule
                ? 'CO_LIVING.TICKETS.BOOKING.RESCHEDULE'
                : mode === 'book'
                ? 'CO_LIVING.TICKETS.BOOKING.BOOK_NOW'
                : 'CO_LIVING.TICKETS.BOOKING.SEND_OPTIONS'
            )
          }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
